import React, { useCallback } from 'react';
import { Typography, TypographyProps } from '@mui/material';
import { useContactDrawer } from '../Contacts/ContactDrawer';

interface ClickableContactNameProps extends Omit<TypographyProps, 'onClick'> {
  contactId?: string;
  contactName: string;
  onClick?: (contactId?: string) => void;
  disabled?: boolean;
}

/**
 * Global rule: Any contact name throughout the app should be clickable and open the contact drawer
 * This component ensures consistent behavior for all contact name interactions
 * 
 * Usage: import ClickableContactName from '../common/ClickableContactName';
 */
const ClickableContactName: React.FC<ClickableContactNameProps> = React.memo(({
  contactId,
  contactName,
  onClick,
  disabled = false,
  sx = {},
  ...typographyProps
}) => {
  const { openDrawer } = useContactDrawer();

  const handleClick = useCallback((e: React.MouseEvent) => {
    const startTime = performance.now();
    
    e.stopPropagation();
    
    if (disabled) {
      console.log('Click disabled');
      return;
    }
    
    // Custom onClick handler takes precedence
    if (onClick) {
      console.log('Using custom onClick handler');
      onClick(contactId);
      return;
    }
    
    // Default behavior: open contact drawer if contactId exists
    if (contactId) {
      openDrawer(contactId);
    } 
    
  }, [contactId, onClick, disabled, openDrawer]);

  return (
    <span
      onClick={handleClick}
      style={{
        cursor: disabled ? 'default' : 'pointer',
        transition: 'color 0.2s ease',
        ...typographyProps.style
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.color = '#1976d2';
          e.currentTarget.style.textDecoration = 'underline';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.color = '';
          e.currentTarget.style.textDecoration = '';
        }
      }}
    >
      {contactName}
    </span>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return prevProps.contactId === nextProps.contactId &&
         prevProps.contactName === nextProps.contactName &&
         prevProps.disabled === nextProps.disabled &&
         prevProps.onClick === nextProps.onClick;
});

ClickableContactName.displayName = 'ClickableContactName';

export default ClickableContactName; 
